import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  BooleanField,
  DateField,
  FunctionField,
  ArrayField,
  Datagrid,
  TopToolbar,
  EditButton,
  DeleteButton,
  ListButton,
  ResourceContextProvider,
} from 'react-admin';
import { Box, Breadcrumbs, Link, Typography, Card, CardContent } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate, useParams } from 'react-router-dom';
import { ColorChip } from '../../components/shared';

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
    <DeleteButton />
  </TopToolbar>
);

const ConfigurationBreadcrumbs = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/configuration')}
          sx={{ cursor: 'pointer', '&:hover': { color: '#1976d2' } }}
        >
          Configuration
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/boards')}
          sx={{ cursor: 'pointer', '&:hover': { color: '#1976d2' } }}
        >
          Boards
        </Link>
        <Typography color="text.primary">Details</Typography>
      </Breadcrumbs>
    </Box>
  );
};

const StageColorChip = ({ record }: { record?: any }) => {
  return <ColorChip color={record?.color} size="large" />;
};

export const BoardShow = () => {
  const { id } = useParams();

  return (
    <ResourceContextProvider value="boards">
      <Show id={id} actions={<ShowActions />}>
        <ConfigurationBreadcrumbs />

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Board Information
            </Typography>
            <SimpleShowLayout>
              <TextField source="name" label="Name" />
              <TextField source="description" label="Description" />
              <TextField source="type" label="Type" />
              <BooleanField source="isActive" label="Active" />
              <DateField source="createdAt" label="Created At" showTime />
              <DateField source="updatedAt" label="Updated At" showTime />
            </SimpleShowLayout>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Board Stages
            </Typography>
            <ArrayField source="stages">
              <Datagrid bulkActionButtons={false}>
                <TextField source="name" label="Stage Name" />
                <TextField source="description" label="Description" />
                <FunctionField
                  source="color"
                  label="Color"
                  render={(record: any) => <StageColorChip record={record} />}
                />
                <TextField source="sortOrder" label="Sort Order" />
                <BooleanField source="isDefault" label="Default" />
                <BooleanField source="isClosedWon" label="Closed Won" />
                <BooleanField source="isClosedLost" label="Closed Lost" />
              </Datagrid>
            </ArrayField>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Board Settings
            </Typography>
            <SimpleShowLayout>
              <BooleanField source="allowMultipleAssignees" label="Allow Multiple Assignees" />
              <BooleanField source="requireDueDates" label="Require Due Dates" />
              <BooleanField source="enableNotifications" label="Enable Notifications" />
              <BooleanField source="archiveCompletedItems" label="Archive Completed Items" />
            </SimpleShowLayout>
          </CardContent>
        </Card>
      </Show>
    </ResourceContextProvider>
  );
};
